// src/components/wall/PenaltyComponent.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { SparklesCore } from "../../../../shared/animations/sparkles/soarklesCore";

const ASSETS = {
  ironbears: {
    leftLogo: "/penalty/ironbears-penalty-logo-left.svg",
    rightLogo: "/penalty/ironbears-penalty-logo-right.svg",
    bannerRight: "/penalty/ironbears-right.svg",
    bannerLeft: "/penalty/ironbears-left.svg",
    scoreBanner: "/penalty/ironbears-penalty-score-banner.svg",
  },
  alphawolves: {
    logo: "/penalty/alphawolves-penalty-logo.svg",
    bannerRight: "/penalty/alphawolves-right.svg",
    bannerLeft: "/penalty/alphawolves-left.svg",
    scoreBanner: "/penalty/alphawolves-penalty-score-banner.svg",
  },
  bullmasters: {
    logo: "/penalty/bullmasters-penalty-logo.svg",
    bannerLeft: "/penalty/bullmasters-left.svg",
    scoreBanner: "/penalty/bullmasters-penalty-score-banner.svg",
  },
  goldeneagles: {
    logo: "/penalty/goldeneagles-penalty-logo.svg",
    bannerRight: "/penalty/goldeneagles-right.svg",
    scoreBanner: "/penalty/goldeneagles-penalty-score-banner.svg",
  },
  penaltyScoreBanner: "/penalty/scoreboard.svg",
  penaltyScoreSlotRight: "/penalty/penalty-score-slot-right.svg",
  penaltyScoreSlot: "/penalty/penalty-score-slot.svg",
  goalSlot: "/penalty/goal.svg",
  missSlot: "/penalty/miss.svg",
  goalSlotRight: "/penalty/goal-right.svg",
  missSlotRight: "/penalty/miss-right.svg",
};

const TEAM_LABELS = {
  ironbears: "IIRONBEARS",
  alphawolves: "ALPHAWOLVES",
  bullmasters: "BULLMASTERS",
  goldeneagles: "GOLDEN EAGLES",
};

const LOGO_PLACEMENT = {
  default: {
    left: { x: -480, y: 0, w: 797, h: 783 },
    right: { x: -200, y: 0, w: 591, h: 783 },
  },
  ironbears: {
    left: { x: 19, y: 20, w: 820, h: 820 },
    right: { x: 19, y: 20, w: 620, h: 820 },
  },
  alphawolves: {
    left: { x: 13, y: 24, w: 760, h: 760 },
    right: { x: 19, y: 20, w: 560, h: 760 },
  },
  bullmasters: {
    left: { x: 19, y: 20, w: 780, h: 780 },
    right: { x: 19, y: 20, w: 600, h: 780 },
  },
  goldeneagles: {
    left: { x: 19, y: 20, w: 780, h: 780 },
    right: { x: 19, y: 24, w: 610, h: 780 },
  },
};

const hasLeft = (k) =>
  k === "ironbears" || k === "alphawolves" || k === "bullmasters";
const hasRight = (k) =>
  k === "ironbears" || k === "alphawolves" || k === "goldeneagles";

function pickSides([a, b]) {
  const opt1 = hasLeft(a) && hasRight(b) ? { left: a, right: b } : null;
  const opt2 = hasLeft(b) && hasRight(a) ? { left: b, right: a } : null;
  return opt1 || opt2 || { left: a, right: b };
}

function teamLogoSrc(teamKey, side) {
  const t = ASSETS[teamKey] || {};
  if (teamKey === "ironbears")
    return side === "left" ? t.leftLogo : t.rightLogo;
  return t.logo || "";
}
function bannerSrc(teamKey, side) {
  const t = ASSETS[teamKey] || {};
  return side === "left" ? t.bannerLeft : t.bannerRight;
}

const BASE_ASSET_URLS = [
  ASSETS.penaltyScoreBanner,
  ASSETS.penaltyScoreSlot,
  ASSETS.penaltyScoreSlotRight,
  ASSETS.goalSlot,
  ASSETS.missSlot,
  ASSETS.goalSlotRight,
  ASSETS.missSlotRight,
];

function preloadImages(urls = []) {
  const unique = [...new Set(urls.filter(Boolean))];
  return Promise.all(
    unique.map(
      (src) =>
        new Promise((resolve) => {
          const img = new Image();
          img.onload = () => resolve(src);
          img.onerror = () => resolve(src);
          img.src = src;
        })
    )
  );
}

export default function PenaltyComponent() {
  const [visible, setVisible] = useState(false);
  const [symbol, setSymbol] = useState("BTCUSDT");
  const [maxScore, setMaxScore] = useState(3);
  const [leftTeam, setLeftTeam] = useState(null);
  const [rightTeam, setRightTeam] = useState(null);
  const [leftScore, setLeftScore] = useState(0);
  const [rightScore, setRightScore] = useState(0);
  const [assetsReady, setAssetsReady] = useState(false);
  const stackRef = useRef([]);
  const [eventTick, setEventTick] = useState(0);
  const bump = () => setEventTick((n) => n + 1);
  const [phase, setPhase] = useState("idle");
  const [animSeed, setAnimSeed] = useState(0);
  const [leftLogoDone, setLeftLogoDone] = useState(false);
  const [rightLogoDone, setRightLogoDone] = useState(false);

  useEffect(() => {
    setLeftLogoDone(false);
    setRightLogoDone(false);
  }, [animSeed]);

  useEffect(() => {
    if (leftLogoDone && rightLogoDone && phase === "logos") setPhase("board");
  }, [leftLogoDone, rightLogoDone, phase]);

  useEffect(() => {
    preloadImages(BASE_ASSET_URLS);
  }, []);

  async function prepareAndStart(nextLeftKey, nextRightKey) {
    setAssetsReady(false);
    const urls = [
      ...BASE_ASSET_URLS,
      teamLogoSrc(nextLeftKey, "left"),
      teamLogoSrc(nextRightKey, "right"),
      bannerSrc(nextLeftKey, "left"),
      bannerSrc(nextRightKey, "right"),
    ];
    await preloadImages(urls);
    setLeftTeam(nextLeftKey);
    setRightTeam(nextRightKey);
    setLeftScore(0);
    setRightScore(0);
    stackRef.current = [];
    bump();
    setAnimSeed((s) => s + 1);
    setPhase("logos");
    setVisible(true);
    setAssetsReady(true);
  }

  useEffect(() => {
    const handleShow = () => {
      if (leftTeam && rightTeam) {
        prepareAndStart(leftTeam, rightTeam);
      } else {
        setVisible(true);
      }
    };
    const handleHide = () => {
      setVisible(false);
      setPhase("idle");
    };
    const handlePair = (e) => {
      const teams = Array.isArray(e.detail?.teams) ? e.detail.teams : [];
      if (teams.length === 2) {
        const { left, right } = pickSides(teams);
        prepareAndStart(left, right);
      }
    };
    const handleSymbol = (e) => {
      const s = String(e.detail?.symbol || "").trim();
      if (s) setSymbol(s);
    };
    const handleMax = (e) => {
      const m = Number(e.detail?.max);
      if (Number.isFinite(m) && m > 0) setMaxScore(m);
    };
    const handleSetScore = (e) => {
      const l = Math.max(0, Number(e.detail?.left) || 0);
      const r = Math.max(0, Number(e.detail?.right) || 0);
      setLeftScore(l);
      setRightScore(r);
      const leftGoals = Array.from({ length: l }, () => ({
        id: cryptoRandom(),
        type: "goal",
        side: "left",
      }));
      const rightGoals = Array.from({ length: r }, () => ({
        id: cryptoRandom(),
        type: "goal",
        side: "right",
      }));
      stackRef.current = [...leftGoals, ...rightGoals];
      bump();
    };
    const handleGoal = (e) => {
      const side = e.detail?.side;
      if (side === "left") {
        setLeftScore((s) => Math.min(maxScore, s + 1));
        stackRef.current.push({
          id: cryptoRandom(),
          type: "goal",
          side: "left",
        });
      } else if (side === "right") {
        setRightScore((s) => Math.min(maxScore, s + 1));
        stackRef.current.push({
          id: cryptoRandom(),
          type: "goal",
          side: "right",
        });
      }
      bump();
    };
    const handleMiss = (e) => {
      const side = e.detail?.side;
      if (side === "left" || side === "right") {
        stackRef.current.push({ id: cryptoRandom(), type: "miss", side });
        bump();
      }
    };
    const handleUndo = () => {
      const last = stackRef.current.pop();
      if (!last) return;
      if (last.type === "goal") {
        if (last.side === "left") setLeftScore((s) => Math.max(0, s - 1));
        if (last.side === "right") setRightScore((s) => Math.max(0, s - 1));
      }
      bump();
    };
    const handleReset = () => {
      setLeftScore(0);
      setRightScore(0);
      stackRef.current = [];
      bump();
    };

    const bc =
      typeof window !== "undefined"
        ? new BroadcastChannel("operator-events")
        : null;
    const bcOnMessage = (m) => {
      const { actionId, payload } = m.data || {};
      if (actionId === "DAY6_SHOW_PANEL") handleShow();
      if (actionId === "DAY6_HIDE_PANEL") handleHide();
      if (actionId === "DAY6_SET_PAIR") handlePair({ detail: payload });
      if (actionId === "DAY6_SET_SYMBOL") handleSymbol({ detail: payload });
      if (actionId === "DAY6_SET_MAX_SCORE") handleMax({ detail: payload });
      if (actionId === "DAY6_SET_SCORE") handleSetScore({ detail: payload });
      if (actionId === "DAY6_GOAL") handleGoal({ detail: payload });
      if (actionId === "DAY6_MISS") handleMiss({ detail: payload });
      if (actionId === "DAY6_UNDO_LAST") handleUndo();
      if (actionId === "DAY6_RESET") handleReset();
    };

    window.addEventListener("DAY6_SHOW_PANEL", handleShow);
    window.addEventListener("DAY6_HIDE_PANEL", handleHide);
    window.addEventListener("DAY6_SET_PAIR", handlePair);
    window.addEventListener("DAY6_SET_SYMBOL", handleSymbol);
    window.addEventListener("DAY6_SET_MAX_SCORE", handleMax);
    window.addEventListener("DAY6_SET_SCORE", handleSetScore);
    window.addEventListener("DAY6_GOAL", handleGoal);
    window.addEventListener("DAY6_MISS", handleMiss);
    window.addEventListener("DAY6_UNDO_LAST", handleUndo);
    window.addEventListener("DAY6_RESET", handleReset);
    if (bc) bc.onmessage = bcOnMessage;

    return () => {
      window.removeEventListener("DAY6_SHOW_PANEL", handleShow);
      window.removeEventListener("DAY6_HIDE_PANEL", handleHide);
      window.removeEventListener("DAY6_SET_PAIR", handlePair);
      window.removeEventListener("DAY6_SET_SYMBOL", handleSymbol);
      window.removeEventListener("DAY6_SET_MAX_SCORE", handleMax);
      window.removeEventListener("DAY6_SET_SCORE", handleSetScore);
      window.removeEventListener("DAY6_GOAL", handleGoal);
      window.removeEventListener("DAY6_MISS", handleMiss);
      window.removeEventListener("DAY6_UNDO_LAST", handleUndo);
      window.removeEventListener("DAY6_RESET", handleReset);
      if (bc) bc.close();
    };
  }, [leftTeam, rightTeam, maxScore, phase]);

  const leftLogo = useMemo(
    () => (leftTeam ? teamLogoSrc(leftTeam, "left") : ""),
    [leftTeam]
  );
  const rightLogo = useMemo(
    () => (rightTeam ? teamLogoSrc(rightTeam, "right") : ""),
    [rightTeam]
  );
  const leftBanner = useMemo(
    () => (leftTeam ? bannerSrc(leftTeam, "left") : ""),
    [leftTeam]
  );
  const rightBanner = useMemo(
    () => (rightTeam ? bannerSrc(rightTeam, "right") : ""),
    [rightTeam]
  );
  const leftEvents = useMemo(
    () => stackRef.current.filter((e) => e.side === "left"),
    [eventTick, leftTeam, rightTeam]
  );
  const rightEvents = useMemo(
    () => stackRef.current.filter((e) => e.side === "right"),
    [eventTick, leftTeam, rightTeam]
  );

  const SLOT_SPACING = 30;
  const LEFT_START = 170;
  const RIGHT_START = 150;
  const leftCount = Math.max(maxScore, leftEvents.length);
  const rightCount = Math.max(maxScore, rightEvents.length);
  const leftXs = Array.from(
    { length: leftCount },
    (_, i) => LEFT_START + i * SLOT_SPACING
  );
  const rightXs = Array.from(
    { length: rightCount },
    (_, i) => RIGHT_START + i * SLOT_SPACING
  );

  const leftCfg =
    (leftTeam && LOGO_PLACEMENT[leftTeam]?.left) || LOGO_PLACEMENT.default.left;
  const rightCfg =
    (rightTeam && LOGO_PLACEMENT[rightTeam]?.right) ||
    LOGO_PLACEMENT.default.right;

  if (!visible || !leftTeam || !rightTeam || !assetsReady) return null;

  return (
    <div className="w-full h-full items-center justify-center flex">
      <AnimatePresence mode="wait">
        {phase !== "idle" && (
          <>
            {leftLogo ? (
              <motion.img
                key={`leftLogo-${animSeed}`}
                src={leftLogo}
                initial={{ x: -260, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                onAnimationComplete={() => setLeftLogoDone(true)}
                className="absolute pointer-events-none z-40 -translate-x-[50%] -translate-y-[50%]"
                style={{
                  left: `${leftCfg.x}%`,
                  top: `${leftCfg.y}%`,
                  width: `${leftCfg.w}px`,
                  height: `${leftCfg.h}px`,
                }}
                alt=""
              />
            ) : null}
            {rightLogo ? (
              <motion.img
                key={`rightLogo-${animSeed}`}
                src={rightLogo}
                initial={{ x: 260, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ duration: 1.2, ease: [0.16, 1, 0.3, 1] }}
                onAnimationComplete={() => setRightLogoDone(true)}
                className="absolute pointer-events-none z-40 -translate-x-[50%] -translate-y-[50%]"
                style={{
                  right: `${rightCfg.x}%`,
                  top: `${rightCfg.y}%`,
                  width: `${rightCfg.w}px`,
                  height: `${rightCfg.h}px`,
                }}
                alt=""
              />
            ) : null}
          </>
        )}
      </AnimatePresence>

      <div className="w-full items-center justify-center flex flex-col gap-[58px]">
        <div className="relative w-[968px] flex items-center justify-center">
          <AnimatePresence>
            {(phase === "board" || phase === "banners" || phase === "live") && (
              <motion.div
                key={`board-${animSeed}`}
                className="relative"
                initial={{ scale: 0.7, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                onAnimationComplete={() =>
                  phase === "board" && setPhase("banners")
                }
              >
                <img
                  className="w-fit relative z-[2]"
                  src={ASSETS.penaltyScoreBanner}
                  alt=""
                />
                <span
                  style={{ fontFamily: "Etemad" }}
                  className="text-[29px] text-[#FFFFFF] font-normal absolute top-1/2 z-[22] left-1/2 -translate-x-1/2 -translate-y-1/2"
                >
                  {symbol}
                </span>
              </motion.div>
            )}
          </AnimatePresence>

          <AnimatePresence>
            {(phase === "banners" || phase === "live") && (
              <>
                <motion.div
                  key={`leftWrap-${animSeed}`}
                  className="absolute left-[10px]"
                  initial={{ x: -140, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
                  onAnimationComplete={() =>
                    phase === "banners" && setPhase("live")
                  }
                >
                  <div className="relative inline-block align-top">
                    {leftBanner ? (
                      <img className="block" src={leftBanner} alt="" />
                    ) : null}
                    <div className="absolute top-[-24px] left-[12px] inset-0 z-[60] pointer-events-none flex items-center justify-center">
                      <span
                        className="uppercase font-extrabold tracking-wide text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)] text-[16px] "
                        style={{ fontFamily: "Etemad" }}
                      >
                        {TEAM_LABELS[leftTeam] || ""}
                      </span>
                    </div>
                    <div className="absolute inset-0 z-50 pointer-events-none">
                      {leftXs.map((x, i) => {
                        const ev = leftEvents[i];
                        const fill = ev?.type;
                        return (
                          <div
                            key={`ls-${i}`}
                            className="absolute bottom-[10px]"
                            style={{
                              left: `${x}px`,
                              transform: "translateX(-50%)",
                            }}
                          >
                            <img src={ASSETS.penaltyScoreSlot} alt="" />
                            {fill && (
                              <motion.img
                                key={ev?.id || `${eventTick}-L-${i}`}
                                src={
                                  fill === "goal"
                                    ? ASSETS.goalSlot
                                    : ASSETS.missSlot
                                }
                                className="absolute inset-0"
                                initial={{
                                  scale: 0.3,
                                  opacity: 0,
                                  filter:
                                    "drop-shadow(0 0 0px rgba(255,255,255,0))",
                                }}
                                animate={{
                                  scale: [0.3, 1.1, 1],
                                  opacity: 1,
                                  filter: [
                                    "drop-shadow(0 0 0px rgba(255,255,255,0))",
                                    "drop-shadow(0 0 10px rgba(255,255,255,0.5))",
                                    "drop-shadow(0 0 0px rgba(255,255,255,0))",
                                  ],
                                }}
                                transition={{
                                  duration: 0.55,
                                  times: [0, 0.6, 1],
                                  ease: [0.2, 0.8, 0.2, 1],
                                }}
                                alt=""
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>

                <motion.div
                  key={`rightWrap-${animSeed}`}
                  className="absolute right-[10px]"
                  initial={{ x: 140, opacity: 0 }}
                  animate={{ x: 0, opacity: 1 }}
                  transition={{ duration: 1.0, ease: [0.16, 1, 0.3, 1] }}
                >
                  <div className="relative inline-block align-top">
                    {rightBanner ? (
                      <img className="block" src={rightBanner} alt="" />
                    ) : null}
                    <div className="absolute top-[-24px] left-[-38px] inset-0 z-[60] pointer-events-none flex items-center justify-center">
                      <span
                        className="uppercase font-extrabold tracking-wide text-white drop-shadow-[0_2px_8px_rgba(0,0,0,0.65)] text-[16px] "
                        style={{ fontFamily: "Etemad" }}
                      >
                        {TEAM_LABELS[rightTeam] || ""}
                      </span>
                    </div>
                    <div className="absolute inset-0 z-50 pointer-events-none">
                      {rightXs.map((x, i) => {
                        const ev = rightEvents[i];
                        const fill = ev?.type;
                        return (
                          <div
                            key={`rs-${i}`}
                            className="absolute bottom-[10px]"
                            style={{ right: `${x}px` }}
                          >
                            <img src={ASSETS.penaltyScoreSlotRight} alt="" />
                            {fill && (
                              <motion.img
                                key={ev?.id || `${eventTick}-R-${i}`}
                                src={
                                  fill === "goal"
                                    ? ASSETS.goalSlotRight
                                    : ASSETS.missSlotRight
                                }
                                className="absolute inset-0"
                                initial={{
                                  scale: 0.3,
                                  opacity: 0,
                                  filter:
                                    "drop-shadow(0 0 0px rgba(255,255,255,0))",
                                }}
                                animate={{
                                  scale: [0.3, 1.1, 1],
                                  opacity: 1,
                                  filter: [
                                    "drop-shadow(0 0 0px rgba(255,255,255,0))",
                                    "drop-shadow(0 0 10px rgba(255,255,255,0.5))",
                                    "drop-shadow(0 0 0px rgba(255,255,255,0))",
                                  ],
                                }}
                                transition={{
                                  duration: 0.55,
                                  times: [0, 0.6, 1],
                                  ease: [0.2, 0.8, 0.2, 1],
                                }}
                                alt=""
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}

function cryptoRandom() {
  try {
    const arr = new Uint32Array(2);
    window.crypto.getRandomValues(arr);
    return `${arr[0].toString(16)}${arr[1].toString(16)}`;
  } catch {
    return `${Date.now()}-${Math.random()}`;
  }
}
